import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import CompanyTile from './../CompanyTile';
import {jobfeedService} from '@jobseeker/common-core/services';
import {
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import {useSelector, shallowEqual} from 'react-redux';
import {Link, Select as LocSelect} from '@jobseeker/lotus/elements';
import {Image} from '@jobseeker/lotus/components/Image';
import {useRouter} from 'next/router';
import {useEffect, useMemo, useRef, useState} from 'react';
import Sticky from 'react-stickynode';
import FilterDialog from '@jobseeker/lotus/components/FilterDialog';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {locations} from '@jobseeker/lotus/components/JobSearchBar/data';
import {ChevronDown} from '@jobseeker/lotus/icons';
import InfiniteScroll from '@jobseeker/lotus/components/InfiniteScroll';
import Loader from '@jobseeker/lotus/components/Loader';
import {categoryOptions, experienceOptions, methodOptions} from '@jobseeker/lotus/constants';
import {
  getKeyFromPath,
  getRefTagAndOrigin,
  handleTracking,
  TrackEventUBA,
  getUbaPageName,
} from '@jobseeker/common-core/utils';
import {Opacity} from '@mui/icons-material';
import {SearchBarHirist} from '@jobseeker/lotus/components';
const SelectIcon = ({className, open}: any) => {
  const theme = useTheme();
  return (
    <Image
      className={className}
      src={ChevronDown}
      alt={'Drop Down Arrow'}
      width={12}
      height={12}
      style={{
        userSelect: 'none',
        padding: '2px',
        zIndex: open ? 2 : 0,
        marginRight: theme.spacing(1.7),
        marginLeft: theme.spacing(1.7),
        marginTop: theme.spacing(0.4),
        transition: '0.3s ease-in-out',
        opacity: 0.5,
      }}
      objectFit="cover"
    />
  );
};

const renderValue = (selected: any, initial: any) => {
  if (selected == initial) {
    return <Typography fontSize={14}>{selected}</Typography>;
  }

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Box
        sx={{
          width: 4,
          height: 4,
          backgroundColor: 'lightgreen',
          borderRadius: '50%',
        }}
      />
      <Typography fontSize={14}>
        {selected?.length > 12 ? `${selected?.slice(0, 10)}...` : selected}
      </Typography>
    </Box>
  );
};
const useScrollPageSync = (pg: any) => {
  const router = useRouter();
  const lastPage = useRef<number>(-1);
  const timeout = useRef<NodeJS.Timeout | null>(null);
  const estimatedPageHeight = 5000;

  useEffect(() => {
    const handleScroll = () => {
      if (timeout.current) clearTimeout(timeout.current);

      timeout.current = setTimeout(() => {
        const scrollY = window.scrollY;
        const calculatedPage = Math.floor(scrollY / estimatedPageHeight);
        const urlPage = Number(router.query.page || 0);

        if (calculatedPage !== lastPage.current && calculatedPage !== urlPage) {
          lastPage.current = calculatedPage;

          // Keep the other query parameters intact, but update the `page`
          const updatedQuery = {
            ...router.query,
            page: calculatedPage + pg,
          };

          router.replace(
            {
              pathname: router.pathname,
              query: updatedQuery,
            },
            undefined,
            {shallow: true}
          );
        }
      }, 150); // delay can be tuned (e.g., 100–300ms)
    };

    window.addEventListener('scroll', handleScroll, {passive: true});
    return () => window.removeEventListener('scroll', handleScroll);
  }, [router.query]); // Adding router.query as a dependency ensures it updates correctly
};

export const StickyContainer = ({children, page}: any) => {
  const isHirist = process.env.NEXT_PUBLIC_APP_NAME === 'hirist';

  const isJobfeed = true;
  const isLarge = useIsLargeDevice();

  if (isHirist && isLarge) {
    return (
      <Sticky
        enabled={isLarge}
        top={154 + 94 - (isJobfeed ? 34 : 0)}
        innerZ={9}
        enableTransforms={false}
      >
        {children}
      </Sticky>
    );
  } else {
    return <>{children}</>;
  }
};
const StyledFormControl = styled(FormControl)(({theme}) => ({
  backgroundColor: 'white',
  borderRadius: 20, // 4 * 4px = 16px
  minWidth: 150,
}));

const StyledSelect = styled(Select)(({theme}) => ({
  borderRadius: 20,
  fontSize: '14px',
  color: 'rgba(0,0,0,0.5)',
  '.MuiInputBase-input': {
    paddingTop: '5px',
    paddingBottom: '5px',
  },
  fontWeight: 500,
  '& .MuiOutlinedInput-notchedOutline': {
    border: '1px solid #7E7E7E33', // Default border color to be always visible
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: '1px solid #7E7E7E33', // Slightly darker on hover
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: '1px solid #7E7E7E33', // Darker when focused
  },
  //   '&.MuiOutlinedInput-root': {
  //     '&:hover': {
  //       border: '0px solid #7E7E7E !important', // Darker when focused
  //     },
}));

// Styled component for MenuItem
const StyledMenuItem = styled(MenuItem)(({theme}) => ({
  fontSize: 16,
  color: '#555555',
  padding: '12px 16px',
  '&:hover': {backgroundColor: '#eff3fa !important'},
  '&.Mui-selected': {
    backgroundColor: '#eff3fa !important',
    '&:hover': {backgroundColor: '#eff3fa !important'},
  },
}));

// Reusable Select Component
export const CategorySelect = ({
  value,
  onChange,
  options,
  renderValue,
}: any) => {
  return (
    <StyledFormControl variant="outlined" size="small">
      <InputLabel
        shrink={false}
        sx={{fontSize: '14px', color: 'rgba(0,0,0,0.5)'}}
      >
        {value ? '' : 'Category'}
      </InputLabel>
      <StyledSelect
        IconComponent={SelectIcon} // Custom SVG Icon
        value={value}
        onChange={onChange}
        displayEmpty
        renderValue={renderValue} // ✅ plugged in here
        MenuProps={{
          anchorOrigin: {vertical: 'bottom', horizontal: 'left'},
          sx: {
            maxHeight: 400,
            minWidth: '200px !important',
            '& .MuiPaper-root': {
              borderRadius: 1.5,
              border: '1px solid #e9e9e9',
              boxShadow: '0 3px 31px 0 rgba(0,0,0,0.09)',
              minWidth: '220px !important',
            },
            '& .MuiBackdrop-root': {backgroundColor: 'transparent !important'},
          },
          transformOrigin: {vertical: 'top', horizontal: 'left'},
        }}
      >
        {options.map((option: any) => (
          <StyledMenuItem key={option.id} value={option.name}>
            {option.name}
          </StyledMenuItem>
        ))}
      </StyledSelect>
    </StyledFormControl>
  );
};
const getUrlWithRefcode = (url: string, refcode: string) => {
  const finalurl = url + `${url.includes('?') ? '&' : '?'}ref=${refcode}`;
  return finalurl;
};
export const BlogCard = (item: any) => {
  return (
    <Link
      href={getUrlWithRefcode(item.link, 'company-page')}
      target="_blank"
      underline="none"
      sx={{
        opacity: 1,
        textDecoration: 'none',
        '&:hover': {textDecoration: 'none'},
      }}
    >
      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: '0 0 310px',
          backgroundColor: '#fff',
          height: '200px',
          boxShadow: 'none',
          ml: 2,
          width: {xxs: 200, md: '200px', xxl: '200px'},
          maxWidth: '500px',
          borderRadius: '15px',
          '@media (max-width: 720px)': {
            flex: '0 0 250px',
            marginBottom: '20px',
          },
          pt: 2,
          pb: 1,
        }}
      >
        <CardMedia
          component={"img"}
          height="180"
          image={item.featuredImage}
          alt={item.title}
          sx={{
            borderRadius: '8px',
            cursor: 'pointer',
            objectFit: 'cover',
            width: {xxs: 200, md: '200px', xxl: '200px'},
            height: 100,
          }}
        />
        <CardContent
          sx={{
            maxHeight: '95px',
            overflow: 'hidden',
            height: '100px',
            cursor: 'pointer',
            padding: '8px',
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontFamily: 'Nunito Sans',
              fontSize: '14px',
              fontWeight: '600',
              lineHeight: 'normal',
              color: '#000',
              wordBreak: 'initial',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {item.title}
          </Typography>
        </CardContent>
      </Card>
    </Link>
  );
};

export const HiristCompanyFeed = (e: any) => {
  const [courses, setCourses] = useState<any[]>([]);
  const isHirist = true;
  const isJobfeed = true;
  const isLarge = useIsLargeDevice();
  const onSearchFormChange = (e: any, value: any) => {
    setLoc(value);
  };

  const onFilterChange = (e: any, value?: any) => {
    if (onSearchFormChange) {
      onSearchFormChange(e, value);
    }
  };

  const router = useRouter();
  const {query} = router; // Get URL params
  const [category, setCategory] = useState(
    query.category || categoryOptions[0].name
  );
  const [expLevel, setExpLevel] = useState(
    query.experience || experienceOptions[0].name
  );
  const [method, setMethod] = useState(query.method || methodOptions[0].name);
  const [loc, setLoc] = useState<any>(
    query.loc
      ? String(query.loc)
          .split(',')
          .map(num => Number(num))
          .filter(num => !isNaN(num))
      : []
  );
  const [pg, setPg] = useState<number>(query.page ? +query.page : 0);

  useScrollPageSync(pg);
  const [companyJobs, setCompanyJobs] = useState<any>(
    e?.companyJobs || {docs: [], hasMore: false}
  );
  const [pageNo, setPageNo] = useState<number>(query.page ? +query.page : 0);
  const [concat, setConcat] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const useUserAgent = () => {
    const [userAgent, setUserAgent] = useState<string>('');

    useEffect(() => {
      if (typeof navigator !== 'undefined') {
        setUserAgent(navigator.userAgent || navigator.vendor || '');
      }
    }, []);

    return userAgent;
  };
  const [clear, setClear] = useState<boolean>(false);

  const userAgent = useUserAgent();
  const [filters, setFilters] = useState({
    method: query.location || methodOptions[0],
    category: query.category || categoryOptions[0],
    experience: query.experience || experienceOptions[0],
    locs: query.loc
      ? String(query.loc)
          .split(',')
          .map(num => Number(num))
          .filter(num => !isNaN(num))
      : [],
  });

  const getDeviceOs = (userAgent: string): string => {
    const isMobile =
      /(iPad|iPhone|Android|webOS|iPod|BlackBerry|IEMobile|Opera Mini|Mobile)/i.test(
        userAgent
      ) || false;

    if (isMobile) {
      if (/android/i.test(userAgent)) {
        return 'android';
      }

      if (/iphone|ipad|ipod|iPhone|ios/.test(userAgent)) {
        return 'ios';
      }
    }

    return 'web';
  };
  useEffect(() => {
    setIsLoading(true);
    const fetchCompanyJobs = async () => {
      try {
        let com_jobs: any = {};
        if (query.keyword && query.keyword.length > 0) {
          com_jobs = await jobfeedService.getCompanySearchList(pageNo, {
            loc: loc.join(','),
            catId:
              (categoryOptions.find(option => option.name === category)?.id ||
                1) -
                1 ==
              0
                ? ''
                : (categoryOptions.find(option => option.name === category)
                    ?.id || 1) - 1,
            keyword,
          });
        } else {
          // Fetch company jobs asynchronously
          com_jobs = await jobfeedService.getCompanyList(pageNo, {
            loc: loc.join(','),
            catId:
              (categoryOptions.find(option => option.name === category)?.id ||
                1) -
                1 ==
              0
                ? ''
                : (categoryOptions.find(option => option.name === category)
                    ?.id || 1) - 1,
          });
        }
        console.log({kk: com_jobs.docs});

        if (concat) {
          setCompanyJobs((prevState: any) => ({
            docs: [...prevState.docs, ...com_jobs.docs], // Append new jobs
            hasMore: com_jobs.hasMore, // Check if there are more jobs
          }));
        } else {
          setCompanyJobs({
            docs: [...com_jobs.docs], // Append new jobs
            hasMore: com_jobs.hasMore, // Check if there are more jobs
          });
        }
      } catch (error) {
        console.error('Error fetching company jobs:', error);
      }
    };

    fetchCompanyJobs();
    const timeout = setTimeout(() => {
      setIsLoading(false);
    }, 4000);
    return () => clearTimeout(timeout);
  }, [pageNo, filters]);
  const onLoadMore = () => {
    setPageNo(pageNo + 1);
    setConcat(true);
  };

  // Update state when URL changes (useful for back/forward navigation)
  useEffect(() => {
    const newFilters = {
      method: query.method || methodOptions[0],
      category: query.category || categoryOptions[0],
      experience: query.experience || experienceOptions[0],
      locs: query.loc
        ? String(query.loc)
            .split(',')
            .map(num => Number(num))
            .filter(num => !isNaN(num))
        : [],
    };
  
    if (JSON.stringify(newFilters) !== JSON.stringify(filters)) {
      setFilters(newFilters);
    }
  }, [query.method, query.category, query.experience, query.loc]);
  const theme = useTheme();
  const [topOffset, setTopOffset] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setTopOffset(
        scrollY > (query.keyword && query.keyword?.length > 0 ? 0 : 200)
          ? Math.min(
              44,
              scrollY - (query.keyword && query.keyword?.length > 0 ? 0 : 200)
            )
          : 0
      );
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setPageNo(pg);
    setConcat(false);
  }, [filters]);
  const [menuOpen, setOpen] = useState<any>(false);
  const setMenuOpen = (value: any) => {
    // console.log(v, menuOpen);
    if (value && value === menuOpen) {
      setMenuOpen(false);
    } else setOpen(value);
  };
  const [blogs, setBlogs] = useState<any[]>([]);
  useEffect(() => {
    const fun = async () => {
      const resp = await jobfeedService.getLatestBlogs(31);
      setBlogs(resp);
    };
    fun();
  }, []);

  console.log({blogs});
  const [keyword, setKeyword] = useState<string>(String(query.keyword) || '');

  const memoizedTiles = useMemo(
    () =>
      companyJobs.docs.map((e: any, index: number) => (
        <>
          <CompanyTile data={e} />{' '}
          {index == 14 && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '16px',
                  my: 2,
                  mr: {xxs: 3, md: 4, xl: 6},
                  ml: 4,
                }}
              >
                <Typography sx={{fontWeight: 600}}>
                  Stay Updated with our Blogs
                </Typography>
                <Link
                  href="https://www.hirist.tech/blog/category/insights/?ref=company-page"
                  target="_blank"
                  sx={{
                    textDecoration: 'none',
                    '&:hover': {textDecoration: 'none'},
                  }}
                >
                  {' '}
                  <Typography sx={{color: '#e9630c', fontWeight: 400}}>
                    View All
                  </Typography>
                </Link>
              </Box>
              <Box
                sx={{
                  overflowX: 'auto',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: {xxs: 'default', md: 'default'},
                  marginBottom: '16px',
                  paddingLeft: {xxs: 0, md: 0},
                  mr: {xxs: 3, md: 4, xl: 6},
                  ml: 4,
                  backgroundColor: 'white',
                  borderRadius: 3,
                }}
              >
                {blogs &&
                  Object.values(blogs)
                    .slice(0, 4)
                    .map((item, index) =>
                      typeof item === 'object' && item !== null ? (
                        <BlogCard key={index} {...item} />
                      ) : null
                    )}
              </Box>
            </Box>
          )}
        </>
      )),
    [companyJobs]
  );
  const {userId, isLoggedIn} = useSelector(
    (state: any) => state.auth,
    shallowEqual
  );
  const selectedCategory = query.category;
  const selectedLoc = query.loc;

  // Convert location to array (in case it's a single value or undefined)
  const locArray = Array.isArray(selectedLoc)
    ? selectedLoc
    : selectedLoc
    ? [selectedLoc]
    : [];

  const shouldRender =
    (selectedCategory && selectedCategory !== categoryOptions[0].name) ||
    locArray.length > 0;
  return (
    <>
      <Box
        display={{xxs: 'block', md: 'flex'}}
        mb={0}
        mr={query.keyword && query.keyword?.length > 0 ? '32px' : 0}
        justifyContent={
          query.keyword && query.keyword?.length > 0 ? 'space-between' : ''
        }
      >
        <Typography
          sx={{
            fontSize: 20,
            color: '#000',
            fontWeight: 600,
            mb: 2,
            ml: {xxs: 1.5, md: 2},
            mt: -1,
          }}
        >
          {query.keyword && query.keyword?.length > 0
            ? `Search for - ${query.keyword}`
            : category == 'Category'
            ? 'Companies'
            : `${category} Companies`}
        </Typography>
        <Box mt={-1} mb={{xxs: 2, md: 0}}>
          {query.keyword && query.keyword?.length > 0 && (
            <SearchBarHirist setKeyword={setKeyword} keyword={keyword} />
          )}
        </Box>
      </Box>
      <StickyContainer>
        <Grid
          container
          sx={{
            px: isHirist ? {xxs: 0, md: 2} : 2,
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            mb: isJobfeed ? {xxs: 2, md: 3} : 3,
            ...(isHirist
              ? {
                  backgroundColor: {md: '#e3e9f0', xxs: 'transparent'},
                  width: {xxs: 'auto', md: '134.4%'},
                  top: isLarge
                    ? `-${104 + (isJobfeed ? 34 - topOffset : 0)}px`
                    : 'unset',
                  py: {xxs: 0, md: 1.5},
                  margin: 'auto',
                  columnGap: 2,
                  justifyContent: 'start',
                  alignItems: 'center',
                  position: !isLarge ? 'fixed' : 'absolute',
                  bottom: isLarge ? 'auto' : '0px',
                  right: isLarge ? 'unset' : '12px',
                  zIndex: isLarge ? 'unset' : 100,
                  mt: isJobfeed ? 0 : 0,
                }
              : {
                  mt: isJobfeed ? {xxs: 2, md: 3} : 3,
                  columnGap: {xxs: 2, md: 3, lg: 4, xl: 12},
                  justifyContent: 'space-between',
                  alignItems: 'start',
                }),
            // [theme.breakpoints.down(1200)]: {
            //   flexWrap: 'wrap',
            // },
          }}
          rowGap={{xxs: 1, md: 3}}
          color="gray.dark"
        >
          {isLarge ? (
            <Box
              display="flex"
              gap={2}
              ml={0}
              alignItems="center"
              borderRadius={16} // Increased border radius
              boxShadow={1} // Slight shadow effect
            >
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#212529',
                  whiteSpace: 'nowrap',
                }}
              >
                Filter By:
              </span>

              {/* Category Filter */}
              <CategorySelect
                value={category}
                onChange={(e: any) => setCategory(e.target.value)}
                options={categoryOptions}
                renderValue={(e: any) =>
                  renderValue(e, categoryOptions[0].name)
                }
              />

              {/* Experience Level Filter */}
              <LocSelect
                menuOpen={menuOpen === 'loc'}
                setMenuOpen={setMenuOpen}
                variant={VersionVariants.VERSION_FOUR}
                options={locations}
                label={'Locations'}
                name="loc"
                value={loc}
                multiple={true}
                onChange={onFilterChange}
                MenuProps={{
                  disablePortal: true,
                }}
              />

              {/* Apply Button (Always Clickable) */}
              <Button
                onClick={() => {
                  const {refText, origin} = getRefTagAndOrigin();
                  TrackEventUBA('filterCompanyListingClick', 'click', {
                    ref: getKeyFromPath('ref') || '',
                    page: getUbaPageName(),
                    filterCategory: category || '',
                    filterLocation: loc.join(',') || '',
                    sessionUsername: sessionStorage.getItem('userId') || '',
                  });
                  handleTracking('jsClickRefineButton', {
                    event_category: 'JobListing',
                    Origin: `${origin}`,
                    UserId: `${userId}`,
                    Device: `${getDeviceOs(userAgent)}`,
                    Ref: `${refText}`,
                    Type: 'company',
                    Location: `${location}`,
                  });
                  setClear(true);
                  router.push(
                    {
                      pathname: router.pathname,
                      query: {
                        category: category, // Use the selected category ID
                        loc: loc.join(','),
                        keyword:
                          keyword && keyword != 'undefined' ? keyword : '',
                      },
                    },
                    undefined,
                    {shallow: true} // Prevents full reload
                  );
                  setMenuOpen(false);
                }}
                sx={{
                  border: '1px solid #e9630c',
                  borderRadius: '16px',
                  padding: '4px 24px',
                  display: 'flex',
                  zIndex: 1200,
                  alignItems: 'center',
                  transition: 'background-color 0.3s ease-in-out',
                  '&:hover': {
                    backgroundColor: '#e9630c', // Prevents hover background change
                    border: '1px solid #e9630c', // Keeps the same border
                    color: '#fff !important',
                  },
                }}
              >
                <Typography
                  className="text"
                  sx={{
                    fontFamily: 'Nunito Sans',
                    fontSize: 14,
                    fontWeight: 500,
                    transition: 'color 0.3s ease-in-out',
                    '&:hover': {
                      color: '#fff !important',
                    },
                  }}
                >
                  Apply
                </Typography>
              </Button>
              {shouldRender && (
                <Typography
                  onClick={() => {
                    const keyword = router.query.keyword;
                    const query = keyword ? {keyword} : {};

                    router.push(
                      {
                        pathname: router.pathname,
                        query,
                      },
                      undefined,
                      {shallow: true}
                    );
                    setCategory(categoryOptions[0].name);
                    setLoc([]);
                  }}
                  fontSize={14}
                  color={'#e9630c'}
                  sx={{cursor: 'pointer', whiteSpace: 'nowrap'}}
                >
                  Clear All
                </Typography>
              )}
            </Box>
          ) : (
            <>
              <Badge
                overlap="circular"
                color={'success'}
                badgeContent=" "
                invisible={!shouldRender}
                sx={{
                  margin: theme.spacing(0.875),
                  '& .MuiBadge-badge': {
                    color: theme.palette.error.dark,
                    width: theme.spacing(1),
                    height: theme.spacing(1),
                    padding: 0,
                    minWidth: theme.spacing(1),
                    top: '20%',
                    right: '28%',
                  },
                }}
              >
                <FilterDialog
                  variant={VersionVariants.VERSION_TWO}
                  onFilter={() => {
                    router.push(
                      {
                        pathname: router.pathname,
                        query: {
                          category: category, // Use the selected category ID
                          method: method,
                          experience: expLevel,
                          loc: loc.join(','),
                          keyword:
                            keyword && keyword != 'undefined' ? keyword : '',
                        },
                      },
                      undefined,
                      {shallow: true} // Prevents full reload
                    );
                  }}
                  isFilter={true}
                  onChange={data => {
                    console.log({data});
                    if (data.category) {
                      setCategory(
                        categoryOptions.find(
                          option => option.id === data.category
                        )?.name || 'Category'
                      );
                    }
                    if (data.experience) {
                      setExpLevel(
                        experienceOptions.find(
                          option => option.id === data.experience
                        )?.name || 'Any Exp. Level'
                      );
                    }
                    if (data.method) {
                      setMethod(
                        methodOptions.find(option => option.id === data.method)
                          ?.name || 'Method'
                      );
                    }
                    if (data.loc) {
                      setLoc(data.loc);
                    }
                  }}
                  formValue={{
                    category: categoryOptions.find(
                      option => option.name === category
                    )?.id,
                    experience: experienceOptions.find(
                      option => option.name === expLevel
                    )?.id,
                    method: methodOptions.find(option => option.name === method)
                      ?.id,
                    loc: loc,
                  }}
                  showCategory={false}
                  showSortBy={false}
                  isLoading={false}
                  industryOptions={[]}
                  company={true}
                />
              </Badge>
            </>
          )}
        </Grid>
      </StickyContainer>
      {isLoading && companyJobs?.docs?.length === 0 ? (
        <>
          {[...new Array(15)].map(index => {
            return (
              <Box display="flex" key={`jobfeed-skel-${index}`}>
                <Skeleton
                  variant="rounded"
                  height={60}
                  sx={{
                    width: {xxs: '75px', md: '65px'},
                    mt: {xxs: 0, md: 1},
                    ml: 2,
                    mb: 1,
                  }}
                />
                <Box display="block" width="100%">
                  <Skeleton
                    variant="rounded"
                    width={'50%'}
                    height={20}
                    sx={{
                      mt: {xxs: 0, md: 1},
                      ml: {xxs: 2, md: 'none'},
                      mb: 1,
                    }}
                  />
                  <Skeleton
                    variant="rounded"
                    width={'90%'}
                    height={20}
                    sx={{
                      mt: {xxs: 0, md: 1},
                      ml: {xxs: 2, md: 'none'},
                      mb: 1,
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </>
      ) : (
        <Box sx={{ml: 1.25}}>
          <InfiniteScroll
            dataLength={companyJobs?.docs?.length}
            next={onLoadMore}
            hasMore={!!companyJobs.hasMore}
            onScroll={undefined}
            loader={
              <Box
                sx={{
                  display: 'flex',
                  textAlign: 'center',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Loader />
              </Box>
            }
            style={{
              scrollbarWidth: 'none',
            }}
          >
            {memoizedTiles}
          </InfiniteScroll>
        </Box>
      )}
      {!isLoading && companyJobs?.docs?.length == 0 && (
        <>
          <h1 style={{fontSize: 24, fontWeight: 600, textAlign: 'center'}}>
            {'Sorry, No result found :('}
            <br></br>
            To get results, Please change your search criteria or filters like
            posting range from the above.
          </h1>
          <Image
            src="https://job-static.hirist.com/V8/static/media/no-jobfeed.7771e408.svg"
            alt="NoNotifications"
            width={350}
            height={400}
            style={{
              objectFit: 'contain',
              width: '70%',
              height: '30%',
              marginLeft: '15%',
            }}
          />
        </>
      )}
    </>
  );
};
